import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Heading } from './common/Catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { TransferReturn } from './clients/rust-api-client';
import { bankService } from './clients/ApiClients';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { canInitiateTransfer } from './constants/UserTypes';
import { formatCurrency, formatDate, getBankErrorParts } from './utils/bankHelpers';
import { Button } from './common/Catalyst/button';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import TransferDialog from './TransferDialog';

interface HoaTransferListProps {
  hoaId: string;
}

/**
 * `TransferReturn` carries `schedule_id` as of 2026-08-31. Both it and
 * `occurrence_date` are null on every manual transfer, which is every
 * row that existed before recurring transfers.
 */
type MaybeScheduled = TransferReturn;

interface AlertState {
  title: string;
  message: string;
}

// Status → Catalyst badge color.
const statusColor = (
  status: string
): 'lime' | 'red' | 'amber' | 'zinc' => {
  switch (status) {
    case 'completed':
      return 'lime';
    case 'failed':
      return 'red';
    case 'pending':
      return 'amber';
    default:
      return 'zinc'; // awaiting_confirmation / unknown
  }
};

const statusLabel = (status: string): string =>
  status
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const HoaTransferList: React.FC<HoaTransferListProps> = ({ hoaId }) => {
  const userType = useMemo(() => getUserTypeFromToken(), []);
  const canInitiate = useMemo(
    () => canInitiateTransfer(userType),
    [userType]
  );

  const [transfers, setTransfers] = useState<MaybeScheduled[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [isTransferOpen, setIsTransferOpen] = useState(false);

  const fetchTransfers = useCallback(async () => {
    try {
      setLoading(true);
      setAlert(null);
      const resp = await bankService.listHoaTransfers({ hoaId });
      setTransfers(resp.data.transfers);
    } catch (err) {
      console.error('Error fetching HOA transfers:', err);
      const { body } = getBankErrorParts(err);
      setAlert({
        title: "Couldn't load transfers",
        message: body || 'Please try again.',
      });
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [hoaId]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  return (
    <>
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-start sm:justify-between sm:space-y-0 mb-4">
        <div>
          <Heading level={4}>Transfers</Heading>
          <Text className="mt-1 text-sm">
            Transfers initiated through this app. A complete record of bank activity can be found in the account's transactions.
          </Text>
        </div>
        {canInitiate && (
          <Button color="sky" onClick={() => setIsTransferOpen(true)}>
            <ArrowsRightLeftIcon className="h-5 w-5 mr-2" />
            New Transfer
          </Button>
        )}
      </div>

      {alert && (
        <ShowAlert
          alertType="error"
          alertTitle={alert.title}
          alertMessage={alert.message}
          isVisible={!!alert}
          onClose={() => setAlert(null)}
          timeout={8000}
          isInline={true}
        />
      )}

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow transition-colors duration-200 overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <LoadingSpinner />
          ) : transfers.length === 0 ? (
            <Text>
              No transfers yet.
              {canInitiate ? ' Use “New Transfer” to move money between this HOA’s accounts.' : ''}
            </Text>
          ) : (
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <Table>
                    <TableHead className="bg-stone-100 dark:bg-gray-600">
                      <TableRow>
                        <TableHeader className="text-base font-semibold">
                          Date
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          From
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          To
                        </TableHeader>
                        <TableHeader className="text-base font-semibold text-right">
                          Amount
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Source
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Status
                        </TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transfers.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell>{formatDate(t.date_created)}</TableCell>
                          <TableCell className="font-medium tabular-nums">
                            {t.source_account_number}
                          </TableCell>
                          <TableCell className="font-medium tabular-nums">
                            {t.dest_account_number}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatCurrency(Number(t.amount))}
                          </TableCell>
                          <TableCell>
                            {/* Which transfers a person set off by hand
                                and which the schedule sent. It matters
                                most for a `pending` row: an unattended
                                one will NOT be retried, and knowing that
                                is the difference between waiting and
                                sending the money a second time. */}
                            {t.schedule_id ? (
                              <Badge color="purple">Scheduled</Badge>
                            ) : (
                              <Text className="text-sm">Manual</Text>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge color={statusColor(t.status)}>
                              {statusLabel(t.status)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <TransferDialog
        open={isTransferOpen}
        onClose={() => setIsTransferOpen(false)}
        hoaId={hoaId}
        onCompleted={fetchTransfers}
      />
    </>
  );
};

export default HoaTransferList;
